import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { Form, Button, Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { set } from 'lodash';
import Loader from '../../Components/Utility/Loader/Loader';
import logo from '../../assets/images/bombiix-logo.svg';
import './SetNewPassword.css';
import { isPasswordCompliant } from '../../constants/password.constants';
import { checkIsLinkExpiredForUser, setPasswordForSupplierUser } from '../Supplier/store/Supplier.action';

function SetNewPassword() {
  const [loginData, setLoginData] = useState({
    newPassword: '',
    cnfPassword: '',
  });
  const { pageLoader } = loginData;
  const dispatch = useDispatch();
  const history = useHistory();
  const [isLinkExpired, setIsLinkExpired] = useState(null);
  // Define a state for error messages
  const [errors, setErrors] = useState({
    newPassword: '',
    cnfPassword: '',
  });
  const [searchParams, setSearchParams] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  // State for password visibility
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showCnfPassword, setShowCnfPassword] = useState(false);
  const { t } = useTranslation('common');
  useEffect(() => {
    const getSearchParams = () => {
      const srchParms = new URLSearchParams(window?.location?.search);
      const setPasswordFor = srchParms.get('setPasswordFor');
      const code = srchParms.get('code');
      const id = srchParms.get('id');
      setSearchParams({ setPasswordFor, code, id });
    };
    getSearchParams();
  }, []);

  useEffect(() => {
    const fetch = async () => {
      if (!searchParams) return;
      const { code, id, setPasswordFor } = searchParams;
      if (code && id && setPasswordFor) {
        const obj = { checkFor: setPasswordFor, userId: id, verificationCode: code };
        const res = await dispatch(checkIsLinkExpiredForUser(obj));
        /**
         * A used or timed-out link is reported through isWrongEmailToken; without
         * it the form renders and can only fail on submit.
         */
        setIsLinkExpired(Boolean(res?.isPasswordAlreadySet || res?.isWrongEmailToken));
      } else {
        setIsLinkExpired(false);
      }
    };
    fetch();
  }, [searchParams, dispatch]);

  const postAPI = async () => {
    const { id, code, setPasswordFor } = searchParams;
    const obj = {
      confirmPassword: loginData.cnfPassword,
      newPassword: loginData.newPassword,
      setPasswordFor,
      userId: id,
      verificationCode: code,
    };
    const res = await dispatch(setPasswordForSupplierUser(obj, t));
    if (res) {
      history.push(`/setup2fa?userId=${id}`);
    }
  };

  const validateForm = (key, val) => {
    const keywords = {
      newPassword: t('SETTINGS.SET_NEW_PASSWORD.NEW_PASSWORD_LABEL'),
      cnfPassword: t('SETTINGS.SET_NEW_PASSWORD.CONFIRM_PASSWORD'),
    };
    if (!val || val.trim().length === 0) {
      setErrors((prev) => ({ ...prev, [key]: `${keywords[key]} ${t('SETTINGS.SET_NEW_PASSWORD.ERROR_REQUIRED')}` }));
      return false;
    }
    if (!isPasswordCompliant(val)) {
      setErrors((prev) => ({
        ...prev,
        [key]: `${keywords[key]} ${t('SETTINGS.SET_NEW_PASSWORD.MUST_CHANGE_MESSAGE')}`,
      }));
      return false;
    }
    if ((key === 'newPassword' && val !== loginData.cnfPassword && !errors.cnfPassword) || val !== loginData.newPassword) {
      setErrors((prev) => ({ ...prev, cnfPassword: t('SETTINGS.SET_NEW_PASSWORD.PASSWORD_MATCH_ERROR') }));
      return false;
    }

    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors[key];
      return updatedErrors;
    });
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    const formError = Object.entries(loginData).reduce((acc, obj) => {
      if (!validateForm(obj[0], obj[1])) {
        return true;
      }
      return acc;
    }, false);
    if (!formError) {
      postAPI();
    }
  };

  const handleInputChange = (keyName, value) => {
    const copyState = loginData;
    set(copyState, keyName, value);
    setLoginData((prevState) => ({ ...prevState, ...copyState }));
    validateForm(keyName, value);
  };

  // Toggle functions for password visibility
  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleCnfPasswordVisibility = () => {
    setShowCnfPassword(!showCnfPassword);
  };

  if (isLinkExpired === null) {
    return <Loader />;
  }

  return (
    <Container>
      <div className={`login-container-bombiix ${isLinkExpired ? 'link-expired-container' : ''}`}>
        {isLinkExpired ? (
          <div className="mt-40">
            <div className="logo-container">
              <div className="small_logo d-block">
                <Link to="/login">
                  <img src={logo} className="img-fluid logo logo-size" alt="logo" />
                </Link>
              </div>
            </div>
            <p className="link-expired-text">{t('SETTINGS.SET_NEW_PASSWORD.LINK_EXPIRED_MESSAGE')}</p>
          </div>
        ) : (
          <>
            <div className="logo-container">
              <img src={logo} className="img-fluid logo-image" alt="logo" />
            </div>
            <div className="mt-40">
              <p className="mb-2 text-medium">{t('SETTINGS.SET_NEW_PASSWORD.PAGE_TITLE')}</p>
              <Form className="login-form" onSubmit={handleSubmit} autoComplete="off">
                <Form.Group controlId="formBasicEmail">
                  <Form.Label className="">
                    {t('SETTINGS.SET_NEW_PASSWORD.NEW_PASSWORD_LABEL')}
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="password-input-container">
                    <Form.Control
                      type={showNewPassword ? 'text' : 'password'}
                      key="newPassword"
                      id="newPassword"
                      name="newPassword"
                      placeholder={t('SETTINGS.SET_NEW_PASSWORD.NEW_PASSWORD_PLACEHOLDER')}
                      className={`password-input ${loginData?.newPassword && 'data-filled'}`}
                      value={loginData?.newPassword}
                      onChange={(e) => handleInputChange('newPassword', e.target.value)}
                      autoComplete="new-Password"
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={toggleNewPasswordVisibility}
                      aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                    >
                      <i className={`fas ${showNewPassword ? 'icon-Password-Eye-Open' : 'icon-Password-Eye-Close'}`} />
                    </button>
                  </div>

                  {isSubmitted && <div className="text-danger err-text">{errors?.newPassword}</div>}
                </Form.Group>

                <Form.Group controlId="formBasicPassword">
                  <Form.Label>
                    {t('SETTINGS.SET_NEW_PASSWORD.CONFIRM_PASSWORD_LABEL')}
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="password-input-container">
                    <Form.Control
                      type={showCnfPassword ? 'text' : 'password'}
                      key="cnfPassword"
                      id="password_login_super_admin"
                      name="password_login_super_admin"
                      autoComplete="new-password"
                      placeholder={t('SETTINGS.SET_NEW_PASSWORD.CONFIRM_PASSWORD_PLACEHOLDER')}
                      className={`password-input ${loginData?.cnfPassword && 'data-filled'}`}
                      value={loginData?.cnfPassword}
                      onChange={(e) => handleInputChange('cnfPassword', e.target.value)}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={toggleCnfPasswordVisibility}
                      aria-label={showCnfPassword ? 'Hide password' : 'Show password'}
                    >
                      <i className={`fas ${showCnfPassword ? 'icon-Password-Eye-Open' : 'icon-Password-Eye-Close'}`} />
                    </button>
                  </div>

                  {isSubmitted && <div className="text-danger err-text">{errors?.cnfPassword}</div>}
                </Form.Group>

                <div className="d-flex justify-content-center align-items-center">
                  <Button disabled={pageLoader} variant="primary" className="btn-theme" type="submit">
                    {t('SETTINGS.SET_NEW_PASSWORD.SUBMIT_BUTTON')}
                  </Button>
                </div>
              </Form>
            </div>
            <div className="password-length-text">{t('SETTINGS.SET_NEW_PASSWORD.PASSWORD_REQUIREMENT')}</div>
          </>
        )}
      </div>
    </Container>
  );
}

export default SetNewPassword;
